import { useState, useEffect, useCallback } from "react";
import { err, Promised, Result, unwrapErr, unwrapOk } from "./types";

type UsePromise = <T, E = never, V = any>(
  promise: (updateTitle: (title?: V) => void) => Promise<Result<T, E>>,
  dependencies?: React.DependencyList
) => Promised<T, E>;
type UsePromiseExt = <T, E = never, V = any>(
  promise: (updateTitle: (title?: V) => void) => Promise<Result<T, E>>,
  dependencies?: React.DependencyList,
  options?: { doInitialFetch?: boolean }
) => { state: Promised<T, E>; execute: () => void };

export const usePromise: UsePromise = (promise, dependencies = []) => {
  type Result = Awaited<ReturnType<typeof promise>>;
  type Data = Parameters<Parameters<typeof promise>[0]>[0];
  const [res, setRes] = useState<Result>();
  const [data, setData] = useState<Data>();

  useEffect(
    () => {
      let mounted = true;
      setRes(undefined);
      promise(setData)
        .then((data) => {
          if (mounted) {
            setRes(data);
          }
        })
        .catch((_err) => {
          if (mounted) {
            setRes(err(_err));
            console.error(_err);
          }
        });
      return () => {
        mounted = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dependencies
  );

  return res
    ? res.type === "ok"
      ? { state: "resolved", res: unwrapOk(res) }
      : { state: "rejected", err: unwrapErr(res) }
    : { state: "pending", data };
};

export const usePromiseExt: UsePromiseExt = (promise, deps = [], options = {}) => {
  const [executionTrigger, setExecutionTrigger] = useState<any>();
  const execute = useCallback(() => setExecutionTrigger({}), [setExecutionTrigger]);
  const state = usePromise(promise, [...deps, executionTrigger]);

  useEffect(() => {
    if (options.doInitialFetch) execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute, state };
};
